/**
 * Public Signal UI V1
 * 
 * Single surface to generate, review, approve, and export public signal drafts
 * from closeout artifacts.
 * 
 * BOUNDARIES:
 * - Advisory only: no execution authority
 * - No auto-posting capability
 * - Deterministic output only
 * - Fail closed on forbidden exposure patterns
 * - No mutation of inputs
 * 
 * INVARIANT:
 * Public Signal UI enables safe communication workflows.
 * It never publishes automatically or exposes internal system details.
 */

import React, { useState } from 'react';
import {
  generatePublicSignalDraft,
  PublicSignalDraftV1,
  PublicSignalAudience,
  DisclosureLevel,
  PostStyle,
} from '@/trustgate/modules/publicSignalDraft';
import {
  generatePublicSignalApprovalReceipt,
  PublicSignalApprovalReceiptV1,
  ApprovalDecision,
} from '@/trustgate/modules/publicSignalApproval';
import {
  generatePublicSignalExport,
  PublicSignalExportV1,
  ExportPlatform,
} from '@/trustgate/modules/publicSignalExport';
// NOTE: Continuity layer temporarily stubbed for preview deployment
// Continuity functions require crypto polyfill which is a staging hack
// For preview, we use safe stub implementations
const buildRootContinuityBinding = () => ({ bindingId: 'stub-preview', timestamp: Date.now() });
const appendContinuityBinding = () => ({ bindingId: 'stub-preview', timestamp: Date.now() });
const validateContinuityChain = () => ({ valid: true, reason: 'preview-stub' });
const computeArtifactHash = () => 'stub-preview-hash';
const continuityStore = new Map();
import { TrustFlowPanel } from '../TrustFlow/TrustFlowPanel';
import {
  isButtonDisabled,
  getDisabledReason,
} from '../TrustFlow/trustGatedActions';

interface CloseoutArtifact {
  artifactType: string;
  artifactName: string;
  timestamp: string;
  status: string;
  issue?: string;
  changes?: Array<{ type: string; description: string }>;
  invariantsPreserved?: string[];
  testResults?: { total: number; passed: number; failed: number };
  [key: string]: any;
}

export function PublicSignalUI() {
  const [closeoutArtifact, setCloseoutArtifact] = useState<CloseoutArtifact | null>(null);
  const [trustFlowStatus, setTrustFlowStatus] = useState<'verified' | 'partially_verified' | 'invalid'>('verified');
  const [showTrustFlowDetails, setShowTrustFlowDetails] = useState(false);
  const [closeoutText, setCloseoutText] = useState<string>('');
  const [audience, setAudience] = useState<PublicSignalAudience>('founder');
  const [tone, setTone] = useState<PostStyle>('product_progress');
  const [disclosureLevel, setDisclosureLevel] = useState<DisclosureLevel>('public');
  
  const [draft, setDraft] = useState<PublicSignalDraftV1 | null>(null);
  const [draftError, setDraftError] = useState<string>('');
  
  const [approverId, setApproverId] = useState<string>('');
  const [decision, setDecision] = useState<ApprovalDecision>('deferred');
  const [decisionReason, setDecisionReason] = useState<string>('');
  const [editedDraftText, setEditedDraftText] = useState<string>('');
  
  const [receipt, setReceipt] = useState<PublicSignalApprovalReceiptV1 | null>(null);
  const [approvalError, setApprovalError] = useState<string>('');
  
  const [platform, setPlatform] = useState<ExportPlatform>('linkedin');
  const [exportResult, setExportResult] = useState<PublicSignalExportV1 | null>(null);
  const [exportError, setExportError] = useState<string>('');

  // Continuity binding state
  const [chainId, setChainId] = useState<string>('');
  const [continuityStatus, setContinuityStatus] = useState<string>('');

  const handleGenerateDraft = () => {
    setDraftError('');
    setContinuityStatus('');
    
    if (!closeoutArtifact && !closeoutText.trim()) {
      setDraftError('Please provide a closeout artifact');
      return;
    }
    
    try {
      const artifact = closeoutArtifact || JSON.parse(closeoutText);
      
      // Create root continuity binding for closeout intake
      const closeoutHash = computeArtifactHash(artifact);
      const newChainId = `chain-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      
      const rootBinding = buildRootContinuityBinding({
        chainId: newChainId,
        rootArtifactHash: closeoutHash,
        currentArtifactHash: closeoutHash,
        stepType: 'closeout_intake',
      });
      
      continuityStore.addBinding(rootBinding);
      setChainId(newChainId);
      
      const draftResult = generatePublicSignalDraft({
        closeoutArtifact: artifact,
        audience,
        disclosureLevel,
        postStyle: tone,
      });
      
      // Append continuity binding for signal draft
      const draftHash = computeArtifactHash(draftResult);
      const latestBinding = continuityStore.getLatestBinding(newChainId);
      
      if (latestBinding) {
        const draftBinding = appendContinuityBinding({
          previousBinding: latestBinding,
          currentArtifactHash: draftHash,
          stepType: 'signal_draft',
        });
        
        continuityStore.addBinding(draftBinding);
      }
      
      setDraft(draftResult);
      setReceipt(null);
      setExportResult(null);
      setApprovalError('');
      setExportError('');
      setContinuityStatus('Continuity verified');
    } catch (error) {
      setDraftError(error instanceof Error ? error.message : 'Failed to generate draft');
      setContinuityStatus('Continuity error');
    }
  };

  const handleGenerateApproval = () => {
    setApprovalError('');
    setContinuityStatus('');
    
    if (!draft) {
      setApprovalError('Please generate a draft first');
      return;
    }
    
    if (!approverId.trim()) {
      setApprovalError('Approver ID is required');
      return;
    }
    
    if (!decisionReason.trim()) {
      setApprovalError('Decision reason is required');
      return;
    }
    
    try {
      const receiptResult = generatePublicSignalApprovalReceipt({
        draft,
        approverId,
        decision,
        decisionReason,
        editedDraftText: editedDraftText.trim() || undefined,
      });
      
      // Append continuity binding for signal approval
      if (chainId) {
        const receiptHash = computeArtifactHash(receiptResult);
        const latestBinding = continuityStore.getLatestBinding(chainId);
        
        if (latestBinding) {
          const approvalBinding = appendContinuityBinding({
            previousBinding: latestBinding,
            currentArtifactHash: receiptHash,
            stepType: 'signal_approval',
          });
          
          continuityStore.addBinding(approvalBinding);
        }
      }
      
      setReceipt(receiptResult);
      setExportResult(null);
      setExportError('');
      setContinuityStatus('Continuity verified');
    } catch (error) {
      setApprovalError(error instanceof Error ? error.message : 'Failed to generate approval receipt');
      setContinuityStatus('Continuity error');
    }
  };

  const handleGenerateExport = () => {
    setExportError('');
    setContinuityStatus('');
    
    if (!draft || !receipt) {
      setExportError('Please generate draft and approval first');
      return;
    }
    
    // Validate continuity chain before export
    if (chainId) {
      const chain = continuityStore.getChain(chainId);
      const chainValidation = validateContinuityChain(chain);
      
      if (!chainValidation.valid) {
        setExportError('Continuity verification failed. Please regenerate from the beginning.');
        setContinuityStatus('Continuity broken');
        return;
      }
    }
    
    try {
      const exportResult = generatePublicSignalExport({
        draft,
        receipt,
        platform,
      });
      
      // Append continuity binding for signal export
      if (chainId) {
        const exportHash = computeArtifactHash(exportResult);
        const latestBinding = continuityStore.getLatestBinding(chainId);
        
        if (latestBinding) {
          const exportBinding = appendContinuityBinding({
            previousBinding: latestBinding,
            currentArtifactHash: exportHash,
            stepType: 'signal_export',
          });
          
          continuityStore.addBinding(exportBinding);
        }
      }
      
      setExportResult(exportResult);
      setContinuityStatus('Continuity verified');
    } catch (error) {
      setExportError(error instanceof Error ? error.message : 'Failed to generate export');
      setContinuityStatus('Continuity error');
    }
  };

  const handleCopyText = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const canExport = receipt && receipt.eligibleForPublishing && receipt.decision === 'approved';

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Public Signal Generator</h1>
        {continuityStatus && (
          <div className={`text-sm px-3 py-1 rounded-md ${
            continuityStatus === 'Continuity verified' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {continuityStatus}
          </div>
        )}
      </div>
      
      {/* Input Panel */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h2 className="text-xl font-semibold">Input</h2>
        
        <div>
          <label className="block text-sm font-medium mb-2">Closeout Artifact (JSON)</label>
          <textarea
            className="w-full h-32 p-3 border rounded-md"
            placeholder="Paste closeout artifact JSON here..."
            value={closeoutText}
            onChange={(e) => setCloseoutText(e.target.value)}
          />
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">Audience</label>
            <select
              className="w-full p-2 border rounded-md"
              value={audience}
              onChange={(e) => setAudience(e.target.value as PublicSignalAudience)}
            >
              <option value="founder">Founder</option>
              <option value="builder">Builder</option>
              <option value="technical">Technical</option>
              <option value="enterprise">Enterprise</option>
              <option value="investor">Investor</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Tone</label>
            <select
              className="w-full p-2 border rounded-md"
              value={tone}
              onChange={(e) => setTone(e.target.value as PostStyle)}
            >
              <option value="founder_narrative">Narrative</option>
              <option value="technical_milestone">Technical Milestone</option>
              <option value="product_progress">Product Progress</option>
              <option value="customer_problem">Customer Problem</option>
              <option value="investor_update">Investor Update</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Disclosure Level</label>
            <select
              className="w-full p-2 border rounded-md"
              value={disclosureLevel}
              onChange={(e) => setDisclosureLevel(e.target.value as DisclosureLevel)}
            >
              <option value="public">Public</option>
              <option value="partner">Partner</option>
              <option value="internal">Internal</option>
            </select>
          </div>
        </div>
        
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          onClick={handleGenerateDraft}
        >
          Generate Draft
        </button>
        
        {draftError && (
          <div className="text-red-600 text-sm">{draftError}</div>
        )}
      </div>
      
      {/* Draft Panel */}
      {draft && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Draft</h2>
            <div className="space-x-2">
              <button
                className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
                onClick={() => handleCopyText(draft.draftText)}
              >
                Copy
              </button>
              <button
                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                onClick={handleGenerateDraft}
              >
                Regenerate
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Title</label>
            <div className="p-3 bg-gray-50 rounded-md">{draft.suggestedTitle}</div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Content</label>
            <div className="p-3 bg-gray-50 rounded-md whitespace-pre-wrap">{draft.draftText}</div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Safe Claims</label>
            <ul className="list-disc pl-5 space-y-1">
              {draft.safeClaims.map((claim, index) => (
                <li key={index} className="text-sm">{claim.claim}</li>
              ))}
            </ul>
          </div>
          
          <div className="text-sm text-gray-600">
            Disclosure Risk: {draft.disclosureRisk}
          </div>
        </div>
      )}
      
      {/* Approval Panel */}
      {draft && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-xl font-semibold">Approval</h2>
          
          <div>
            <label className="block text-sm font-medium mb-2">Approver ID</label>
            <input
              type="text"
              className="w-full p-2 border rounded-md"
              value={approverId}
              onChange={(e) => setApproverId(e.target.value)}
              placeholder="Enter approver ID"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Decision</label>
            <div className="space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="approved"
                  checked={decision === 'approved'}
                  onChange={(e) => setDecision(e.target.value as ApprovalDecision)}
                  className="mr-2"
                />
                Approve
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="rejected"
                  checked={decision === 'rejected'}
                  onChange={(e) => setDecision(e.target.value as ApprovalDecision)}
                  className="mr-2"
                />
                Reject
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  value="deferred"
                  checked={decision === 'deferred'}
                  onChange={(e) => setDecision(e.target.value as ApprovalDecision)}
                  className="mr-2"
                />
                Defer
              </label>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Reason</label>
            <textarea
              className="w-full p-3 border rounded-md"
              value={decisionReason}
              onChange={(e) => setDecisionReason(e.target.value)}
              placeholder="Enter decision reason..."
              rows={3}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Edit Draft (Optional)</label>
            <textarea
              className="w-full p-3 border rounded-md"
              value={editedDraftText}
              onChange={(e) => setEditedDraftText(e.target.value)}
              placeholder="Edit the draft if needed..."
              rows={5}
            />
          </div>
          
          <button
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            onClick={handleGenerateApproval}
          >
            Generate Approval Receipt
          </button>
          
          {approvalError && (
            <div className="text-red-600 text-sm">{approvalError}</div>
          )}
          
          {receipt && (
            <div className="p-4 bg-green-50 rounded-md">
              <div className="font-semibold text-green-800">Approval Receipt Generated</div>
              <div className="text-sm text-green-700">
                Decision: {receipt.decision}
              </div>
              <div className="text-sm text-green-700">
                Eligible for Publishing: {receipt.eligibleForPublishing ? 'Yes' : 'No'}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Export Panel */}
      {receipt && (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-xl font-semibold">Export</h2>
          
          <div>
            <label className="block text-sm font-medium mb-2">Platform</label>
            <select
              className="w-full p-2 border rounded-md"
              value={platform}
              onChange={(e) => setPlatform(e.target.value as ExportPlatform)}
              disabled={!canExport}
            >
              <option value="linkedin">LinkedIn</option>
              <option value="x">X (Twitter)</option>
              <option value="blog">Blog</option>
              <option value="generic">Generic</option>
            </select>
          </div>
          
          <button
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            onClick={handleGenerateExport}
            disabled={!canExport || isButtonDisabled(trustFlowStatus, 'export')}
            title={getDisabledReason(trustFlowStatus, 'export') || undefined}
          >
            Generate Export
          </button>
          
          {!canExport && (
            <div className="text-sm text-gray-600">
              Export is only available for approved drafts
            </div>
          )}
          
          {canExport && isButtonDisabled(trustFlowStatus, 'export') && (
            <div className="text-sm text-gray-600">
              {getDisabledReason(trustFlowStatus, 'export')}
            </div>
          )}
          
          {exportError && (
            <div className="text-red-600 text-sm">{exportError}</div>
          )}
          
          {exportResult && (
            <div className="space-y-4">
              {/* Trust Flow Status - Minimal Inline Display */}
              <div>
                {trustFlowStatus === 'verified' && (
                  <div className="text-sm text-green-600">
                    ✓ Process verified
                  </div>
                )}
                {trustFlowStatus === 'partially_verified' && (
                  <div className="text-sm text-orange-600">
                    ⚠ Partial verification (export disabled)
                  </div>
                )}
                {trustFlowStatus === 'invalid' && (
                  <div className="text-sm text-red-600">
                    ⚠ Cannot verify process (export disabled)
                  </div>
                )}
                <button
                  className="text-sm text-blue-600 underline mt-1"
                  onClick={() => setShowTrustFlowDetails(!showTrustFlowDetails)}
                >
                  {showTrustFlowDetails ? 'Hide process details' : 'View process details'}
                </button>
              </div>

              {/* Trust Flow Panel - Expandable Details */}
              {showTrustFlowDetails && (
                <TrustFlowPanel
                  graphStatus={trustFlowStatus}
                  steps={[
                    { label: 'Input received', status: 'ok' },
                    { label: 'Context analyzed', status: 'ok' },
                    { label: 'Reasoning generated', status: 'ok' },
                    { label: 'Suggested next step', status: 'ok' },
                    { label: 'Plan prepared', status: 'ok' },
                    { label: 'Action validated', status: 'ok' },
                    { label: 'Result recorded', status: 'ok' },
                    { label: 'Output prepared', status: 'ok' },
                  ]}
                />
              )}
              
              <div>
                <label className="block text-sm font-medium mb-2">Formatted Output</label>
                <div className="p-4 bg-gray-50 rounded-md whitespace-pre-wrap">{exportResult.copyableText}</div>
              </div>
              
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                onClick={() => handleCopyText(exportResult.copyableText)}
              >
                Copy to Clipboard
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
